import React, { useState, useEffect, useCallback } from 'react';
import { RocketLaunchIcon, InstallIcon, XMarkIcon, IosShareIcon, ComputerDesktopIcon, EllipsisVerticalIcon, PlusIcon } from './Icons';

const getPlatform = () => {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window);
    const isAndroid = /Android/i.test(ua);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    const isDesktop = !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    return { isIOS, isAndroid, isDesktop, isStandalone };
};

const InstructionStep: React.FC<{ icon: React.ReactNode; text: React.ReactNode; isLast?: boolean }> = ({ icon, text, isLast = false }) => (
    <>
        <div className="flex items-center gap-4 text-left">
            <div className="flex-shrink-0 bg-slate-700 rounded-lg p-2 text-blue-400">{icon}</div>
            <div className="text-slate-200">{text}</div>
        </div>
        {!isLast && <div className="h-4 border-l-2 border-dashed border-slate-600 self-start ml-6"></div>}
    </>
);


const InstallPwaPrompt: React.FC = () => {
    const [installPrompt, setInstallPrompt] = useState<any>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [platformInfo] = useState(getPlatform());

    useEffect(() => {
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setInstallPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }, []);

    useEffect(() => {
        if (platformInfo.isStandalone) {
            setIsVisible(false);
            return;
        }
        
        const dismissedThisSession = sessionStorage.getItem('pwaInstallDismissed');

        if (!dismissedThisSession) {
            if (installPrompt || platformInfo.isIOS || platformInfo.isAndroid || platformInfo.isDesktop) {
                const timer = setTimeout(() => setIsVisible(true), 3000);
                return () => clearTimeout(timer);
            }
        }
    }, [installPrompt, platformInfo]);

    const handleInstallClick = useCallback(async () => {
        if (!installPrompt) return;

        try {
            await installPrompt.prompt();
            // The browser will handle the rest.
        } catch (error) {
            console.error('Error during PWA installation prompt:', error);
        } finally {
            setIsVisible(false);
            setInstallPrompt(null);
        }
    }, [installPrompt]);

    const handleDismissClick = useCallback(() => {
        sessionStorage.setItem('pwaInstallDismissed', 'true');
        setIsVisible(false);
    }, []);

    if (!isVisible) {
        return null;
    }

    let content;

    if (installPrompt) {
        content = (
             <div className="flex items-center gap-4">
                <RocketLaunchIcon className="w-12 h-12 text-blue-400 flex-shrink-0" />
                <div className="flex-grow">
                    <h3 id="pwa-install-title" className="font-bold text-lg">Unleash the Power of the App</h3>
                    <p className="text-sm text-slate-300">Install to your device for faster access and a premium offline experience.</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <button
                        onClick={handleInstallClick}
                        className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-5 rounded-lg flex items-center gap-2 transition-all duration-300 transform hover:scale-105 animate-glow shadow-lg"
                    >
                        <InstallIcon className="w-5 h-5"/>
                        Install
                    </button>
                </div>
            </div>
        );
    } else if (platformInfo.isIOS) {
        content = (
            <div className="flex flex-col items-center text-center">
                <h3 id="pwa-install-title" className="font-bold text-lg mb-4">Install App on your iPhone/iPad</h3>
                <div className="flex flex-col items-center gap-2">
                    <InstructionStep icon={<IosShareIcon className="w-6 h-6"/>} text={<p>Tap the <strong>Share</strong> icon in Safari.</p>} />
                    <InstructionStep icon={<PlusIcon className="w-6 h-6" />} text={<p>Scroll down and tap <strong>'Add to Home Screen'</strong>.</p>} isLast/>
                </div>
            </div>
        );
    } else if (platformInfo.isAndroid) {
         content = (
            <div className="flex flex-col items-center text-center">
                <h3 id="pwa-install-title" className="font-bold text-lg mb-4">Install App on Android</h3>
                <div className="flex flex-col items-center gap-2">
                    <InstructionStep icon={<EllipsisVerticalIcon className="w-6 h-6"/>} text={<p>Tap the <strong>Menu</strong> icon (3 dots) in your browser.</p>} />
                    <InstructionStep icon={<InstallIcon className="w-6 h-6" />} text={<p>Tap <strong>'Install app'</strong> or <strong>'Add to Home Screen'</strong>.</p>} isLast/>
                </div>
            </div>
        );
    } else if (platformInfo.isDesktop) {
        content = (
            <div className="flex flex-col items-center text-center">
                <h3 id="pwa-install-title" className="font-bold text-lg mb-4">Install App on your PC</h3>
                <div className="flex flex-col items-center gap-2">
                    <InstructionStep icon={<ComputerDesktopIcon className="w-6 h-6"/>} text={<p>Look for the <strong>Install icon</strong> in your browser's address bar.</p>} isLast/>
                </div>
            </div>
        );
    } else {
        return null;
    }

    return (
        <div role="dialog" aria-labelledby="pwa-install-title" className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up-fade-in no-print">
            <div className="container mx-auto max-w-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-slate-100 rounded-xl shadow-2xl p-5 border border-blue-500/30">
                <div className="relative">
                    <button
                        onClick={handleDismissClick}
                        className="absolute -top-2 -right-2 p-1.5 text-slate-500 hover:text-white transition-colors bg-slate-700/50 rounded-full"
                        aria-label="Dismiss install prompt"
                    >
                       <XMarkIcon className="w-5 h-5" />
                    </button>
                    {content}
                </div>
            </div>
        </div>
    );
};

export default InstallPwaPrompt;